import {
  Box,
  HStack,
  SkeletonCircle,
  SkeletonText,
  Spacer,
  Spinner,
  Stack,
} from '@cardboard-ui/react';
import { t, Trans } from '@lingui/macro';
import {
  ItemLocationInfo,
  useHasLocationInfo,
} from 'apps/TenantApp/components/ItemLocationInfo';
import { TextLink } from 'apps/TenantApp/components/TextLink';
import { Card, CardContent } from 'components/cards/Card';
import { SimpleCard } from 'components/cards/SimpleCard';
import { ErrorMessage } from 'components/ErrorMessage';
import { FileAltIcon } from 'components/icons';
import React, { PropsWithChildren, Suspense } from 'react';
import { ErrorBoundary } from 'utils/errors';
import {
  graphql,
  useFragment,
  useMutation,
  useSafeRelay,
} from 'utils/graphClient';
import { TodoAssignee } from './TodoAssignee';
import { TodoDueDate } from './TodoDueDate';
import { TodoOptionsMenu } from './TodoOptionsMenu';
import { TodoStatusToggle } from './TodoStatusToggle';
import { TodoCard_page$key } from './__generated__/TodoCard_page.graphql';
import { TodoCard_todo_Query } from './__generated__/TodoCard_todo_Query.graphql';
import {
  TodoCard_transitionTodo_Mutation,
  TodoState,
} from './__generated__/TodoCard_transitionTodo_Mutation.graphql';
import { TodoOptionsMenu_todo$key } from './__generated__/TodoOptionsMenu_todo.graphql';

export type TodoStatus = TodoState;

const transitionTodoMutation = graphql`
  mutation TodoCard_transitionTodo_Mutation($id: ID!, $state: TodoState!) {
    transitionTodo(input: { id: $id, state: $state }) {
      todoUpdated {
        ... on Todo {
          id
          __typename
          ...TodoDueDate_todo
          ...TodoAssignee_todo
          text
          documentNode {
            id
            ...TodoCard_page
          }
          assignee {
            id
          }
          state
          dueDate
          publishState
          isListed
          canTransition {
            value
          }
          canDelete {
            value
          }
        }
      }
    }
  }
`;

const nodeQuery = graphql`
  query TodoCard_todo_Query($id: ID!) {
    node(id: $id) {
      ... on Todo {
        id
        __typename
        ...TodoDueDate_todo
        ...TodoAssignee_todo
        ...TodoOptionsMenu_todo
        text
        documentNode {
          id
          ...TodoCard_page
        }
        assignee {
          id
        }
        state
        dueDate
        publishState
        isListed
        canTransition {
          value
        }
        canDelete {
          value
        }
      }
    }
  }
`;

export type TodoCardProps = PropsWithChildren<{
  id?: string;
  editor?: TodoCardEditorProps;
}>;

export interface TodoCardEditorProps {
  editMode: boolean;
  deleteEditorTodo(): void;
}

export const TodoCard: React.FC<TodoCardProps> = (props) => {
  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Suspense fallback={<TodoCardSkeleton editor={!!props.editor} />}>
        <UnsuspendedTodoCard {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};

const TodoCardSkeleton = ({ editor }: { editor: boolean }) => (
  <Card
    my={2}
    overflow="visible"
    isNotClickable={!!editor}
    data-qa="todo-card-skeleton"
  >
    <CardContent>
      <Stack spacing={3}>
        <HStack>
          <Box>
            <SkeletonCircle size="5" borderRadius={0} />
          </Box>
          <Box flex={1}>
            <SkeletonText noOfLines={1} />
          </Box>
          <Box>
            <SkeletonCircle size="5" />
          </Box>
        </HStack>
        <HStack w="100%">
          <Spacer />
          <SkeletonCircle size="5" />
          <SkeletonCircle size="5" />
        </HStack>
      </Stack>
    </CardContent>
  </Card>
);

const UnsuspendedTodoCard: React.FC<TodoCardProps> = ({
  id = '',
  children,
  editor,
  // updateEditorContent,
}) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const { node } = useLazyLoadQuery<TodoCard_todo_Query>(nodeQuery, { id });

  if (node && node.__typename !== 'Todo') {
    throw new Error('Node is not a TODO');
  }

  let state: TodoState;

  if (node) {
    state = node.state;
  } else {
    state = 'UNCHECKED';
  }

  const [transitionTodo, isTransitioning] =
    useMutation<TodoCard_transitionTodo_Mutation>(transitionTodoMutation);

  const onTransition = (state: TodoState) => {
    if (id) {
      transitionTodo({ variables: { id, state } });
    }
  };

  return (
    <TodoCardPlain
      state={state}
      page={!editor && !!node && <PageContext page={node.documentNode} />}
      onTransition={onTransition}
      dueDate={node && <TodoDueDate todo={node} />}
      isTransitioning={isTransitioning}
      editor={editor}
      assignee={node && <TodoAssignee todo={node} />}
      todo={node || undefined}
      canTransition={node?.canTransition.value || false}
      canDelete={node?.canTransition.value || false}
    >
      {children}
    </TodoCardPlain>
  );
};

type TodoCardPlainProps = PropsWithChildren<{
  state: TodoStatus;
  page?: React.ReactNode;
  dueDate?: React.ReactNode;
  assignee?: React.ReactNode;
  isTransitioning?: boolean;
  onTransition: (status: TodoStatus) => void;
  editMode?: boolean;
  todo?: TodoOptionsMenu_todo$key;
  deleteEditorTodo?: () => void;

  canTransition: boolean;
  canDelete: boolean;
  editor?: TodoCardEditorProps;
}>;

const TodoCardPlain: React.FC<TodoCardPlainProps> = ({
  state,
  page,
  dueDate,
  assignee,
  onTransition,
  children,
  isTransitioning = false,
  todo,
  canTransition,
  editor,
}) => {
  return (
    <Card
      my={2}
      overflow="visible"
      isNotClickable={!!editor}
      data-qa="todo-card"
    >
      <CardContent>
        <Stack spacing={3}>
          <HStack align="baseline">
            {isTransitioning && <Spinner size="xs" />}
            <Box>
              <TodoStatusToggle
                onStatusChange={onTransition}
                status={state}
                canTransition={canTransition}
              />
            </Box>
            <Box flex={1}>{children}</Box>

            <HStack spacing={0.5}>
              {todo && (
                <TodoOptionsMenu
                  todo={todo}
                  editor={editor}
                  onTransition={onTransition}
                />
              )}
            </HStack>
          </HStack>
          {(page || dueDate || assignee) && (
            <HStack w="100%">
              <Spacer />
              {!!dueDate && dueDate}
              {!!assignee && assignee}
            </HStack>
          )}
        </Stack>
      </CardContent>
      {page && (
        <Box mt={1} w="100%" bg="gray.50" _dark={{ bg: 'gray.900' }} p={3}>
          {page}
        </Box>
      )}
    </Card>
  );
};

const ErrorFallback = () => {
  return (
    <SimpleCard>
      <ErrorMessage description={t`Error loading task.`} />
    </SimpleCard>
  );
};

const pageFragment = graphql`
  fragment TodoCard_page on FolderItem {
    id
    name
    content {
      __typename
    }
    ...ItemLocationInfo_item
  }
`;

interface PageContextProps {
  page: TodoCard_page$key;
}

export const PageContext: React.FC<PageContextProps> = ({ page }) => {
  const { id, name, content, ...fragments } = useFragment(pageFragment, page);
  const hasLocationInfo = useHasLocationInfo({ item: fragments });

  if (content.__typename !== 'Page') {
    return null;
  }

  const documentName = (
    <HStack display="inline-flex" spacing={0.5} fontSize="xs">
      <FileAltIcon color="blue.500" />
      <TextLink toItemViewer={id}>{name}</TextLink>
    </HStack>
  );
  const locationName = <ItemLocationInfo item={fragments} />;

  return (
    <Box fontSize="xs">
      {hasLocationInfo ? (
        <Trans
          context="todos"
          comment="Describes the location of a todo. e.g.: From 'my document' in 'SpaceName'"
        >
          from {documentName} in {locationName}
        </Trans>
      ) : (
        <Trans
          context="todos"
          comment="Describes the location of a todo. e.g.: From 'my document'"
        >
          from {documentName}
        </Trans>
      )}
    </Box>
  );
};
